/*
 * OMNANA: statutory guarantee notice dialog (see snippets/om_guarantee-notice-dialog.liquid).
 *
 * - One dialog per page; triggers are found by delegation, so cart drawer / cart page
 *   re-renders (Section Rendering API replaces their markup) never lose the handler.
 * - The official artwork is fetched from the provider only when the dialog is first
 *   opened (it is ~600 KB), never bundled with the theme.
 * - The provider's official text version is offered below the image as a text alternative.
 * - If the provider cannot be reached the shopper gets a clear message, a retry and the
 *   official Your Europe link instead of a broken image. Nothing here touches the cart.
 */
(function () {
  'use strict';

  var dialog = document.querySelector('[data-om-gn-dialog]');
  if (!dialog || dialog.dataset.omGnReady) return;
  dialog.dataset.omGnReady = 'true';

  var img = dialog.querySelector('[data-om-gn-img]');
  var figure = dialog.querySelector('[data-om-gn-figure]');
  var loading = dialog.querySelector('[data-om-gn-loading]');
  var error = dialog.querySelector('[data-om-gn-error]');
  var body = dialog.querySelector('[data-om-gn-body]');
  var LOAD_TIMEOUT = 12000;
  var opener = null;
  var state = 'idle'; // idle | loading | loaded | failed
  var attempts = 0;
  var timer = null;

  function show(which) {
    loading.hidden = which !== 'loading';
    figure.hidden = which !== 'loaded';
    error.hidden = which !== 'failed';
  }

  function settle(next) {
    window.clearTimeout(timer);
    state = next;
    show(next);
    dialog.setAttribute('data-om-gn-state', next);
  }

  function load() {
    if (state === 'loading' || state === 'loaded') return;
    var src = dialog.getAttribute('data-om-gn-src');
    if (!src) return settle('failed');

    settle('loading');
    // No cache-busting parameter on retry: the provider redirects unknown parameters.
    // Clearing src first makes the browser issue a fresh request for the same URL.
    if (attempts > 0) img.removeAttribute('src');
    attempts += 1;
    loadText();

    timer = window.setTimeout(function () {
      if (state === 'loading') {
        img.removeAttribute('src');
        settle('failed');
      }
    }, LOAD_TIMEOUT);
    img.src = src;
  }

  // Official wording from the provider (same language resolution as the image). Optional:
  // if it cannot be fetched the toggle simply stays hidden, the image remains the notice.
  var textWrap = dialog.querySelector('[data-om-gn-text]');
  var textBody = dialog.querySelector('[data-om-gn-text-body]');
  var textState = 'idle';
  function loadText() {
    var textSrc = dialog.getAttribute('data-om-gn-text-src');
    if (!textSrc || !textWrap || !window.fetch || textState !== 'idle') return;
    textState = 'loading';
    fetch(textSrc)
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var lang = res.headers.get('X-Notice-Locale') || res.headers.get('Content-Language');
        return res.text().then(function (text) {
          if (text.length < 200) throw new Error('implausible text');
          textBody.textContent = '';
          text.split(/\n\s*\n/).forEach(function (para, index) {
            var el = document.createElement(index === 0 ? 'h3' : 'p');
            el.textContent = para.trim();
            if (el.textContent) textBody.appendChild(el);
          });
          if (lang) textBody.setAttribute('lang', lang);
          textWrap.hidden = false;
          textState = 'loaded';
        });
      })
      .catch(function () {
        textState = 'idle';
      });
  }

  img.addEventListener('load', function () {
    // A 0x0 "image" means the provider answered with something that is not the notice.
    if (img.naturalWidth > 0) settle('loaded');
    else settle('failed');
  });
  img.addEventListener('error', function () {
    if (state === 'loading') settle('failed');
  });

  function open(trigger) {
    opener = trigger;
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('om-gn-open');
    body.scrollTop = 0;
    body.scrollLeft = 0;
    load();
  }

  function close() {
    if (typeof dialog.close === 'function') dialog.close();
    else {
      dialog.removeAttribute('open');
      onClose();
    }
  }

  function onClose() {
    document.documentElement.classList.remove('om-gn-open');
    // The cart may have re-rendered while the dialog was open; fall back to the live trigger.
    var target = opener && document.contains(opener) ? opener : document.querySelector('[data-om-gn-open]');
    opener = null;
    if (target) target.focus();
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-om-gn-open]');
    if (trigger) {
      event.preventDefault();
      open(trigger);
      return;
    }
    if (!dialog.open) return;
    if (event.target.closest('[data-om-gn-close]')) return close();
    if (event.target.closest('[data-om-gn-retry]')) return load();
    // Click on the backdrop (the dialog element itself, outside its content box).
    if (event.target === dialog) close();
  });

  dialog.addEventListener('close', onClose);

  // Centred service rows (cart drawer): when the row wraps, the item that starts a new line
  // must not show its leading dot. CSS cannot know where a centred line breaks, so flag it.
  function markLineStarts(row) {
    var items = row.querySelectorAll('.om-service-row__item');
    for (var i = 0; i < items.length; i++) {
      var startsLine = i > 0 && items[i].offsetTop > items[i - 1].offsetTop;
      items[i].classList.toggle('is-line-start', startsLine);
    }
  }
  if (typeof ResizeObserver === 'function') {
    var rowObserver = new ResizeObserver(function (entries) {
      entries.forEach(function (entry) {
        markLineStarts(entry.target);
      });
    });
    document.querySelectorAll('.om-service-row').forEach(function (row) {
      rowObserver.observe(row);
    });
  }

  // Escape closes only this dialog. The dialog closes on keydown and focus returns to the
  // trigger, so the matching keyup would land inside the cart drawer and close that too
  // (the theme closes the drawer on Escape keyup). Swallow exactly that one keyup.
  var swallowEscapeKeyup = false;
  dialog.addEventListener('keydown', function (event) {
    if (event.code === 'Escape') swallowEscapeKeyup = true;
  });
  document.addEventListener(
    'keyup',
    function (event) {
      if (event.code !== 'Escape' || !swallowEscapeKeyup) return;
      swallowEscapeKeyup = false;
      event.stopPropagation();
    },
    true
  );
})();
